import React from "react"

import CaptionApi from "utils/api/caption"
import { AuthContext } from "contexts/AuthContext"

export const CaptionContext = React.createContext({
  captions: [],
  lastSyncedAt: null,
  create: params => {},
  fetchAll: () => {},
  fetch: id => {},
  sync: params => {},
  update: (id, params) => {},
  delete: id => {},
  reset: () => {},
})

export const CaptionContextProvider = ({ children }) => {
  const { isPro, rejectionMessage } = React.useContext(AuthContext)
  const [lastSyncedAt, setLastSyncedAt] = React.useState(null)
  const [captions, setCaptions] = React.useState([])

  //
  // Create a new caption
  //

  const create = React.useCallback(
    params => {
      return new Promise(async (resolve, reject) => {
        try {
          const { localId, name, content } = params
          let response = await CaptionApi.create({ localId, name, content })
          setCaptions([response.data.data, ...captions])
          resolve(response.data.data)
        } catch (err) {
          reject(err)
        }
      })
    },
    [captions]
  )

  //
  // Get all captions for the current user
  //

  const fetchAll = React.useCallback(() => {
    return new Promise(async (resolve, reject) => {
      try {
        // Return our existing ones if we have them!
        if (captions && captions.length) {
          return resolve(captions)
        }

        let response = await CaptionApi.all()
        setCaptions(response.data.data)
        resolve(response.data.data)
      } catch (err) {
        reject(err)
      }
    })
  }, [captions])

  //
  // Get a single caption by id
  //

  const fetch = React.useCallback(
    id => {
      return new Promise(async (resolve, reject) => {
        try {
          // If we've already loaded this one just pull it from our cache!
          const existingCaption = captions && captions.find(caption => caption.id === id)
          if (existingCaption) return resolve(existingCaption)
          // If we haven't, load it from the api
          let response = await CaptionApi.fetch(id)
          resolve(response.data.data)
        } catch (err) {
          reject(err)
        }
      })
    },
    [captions]
  )

  //
  // Update a specific caption
  //

  const update = React.useCallback(
    (id, params) => {
      return new Promise(async (resolve, reject) => {
        try {
          // If we haven't, load it from the api
          let response = await CaptionApi.update(id, params)
          const caption = response.data.data
          // If we've stored this item, update it in place!
          const index = (captions || []).findIndex(localCaption => localCaption.id === caption.id)
          if (index !== -1) {
            const newCaptions = [...captions]
            newCaptions.splice(index, 1, { ...newCaptions[index], ...caption })
            setCaptions(newCaptions)
          }
          // Return our update result
          resolve(caption)
        } catch (err) {
          reject(err)
        }
      })
    },
    [captions]
  )

  //
  // Sync the current saved data with data from the server
  //

  const sync = React.useCallback(
    caption => {
      return new Promise(async (resolve, reject) => {
        try {
          // Make sure we only send up the things we need for the caption we're updating
          const captionData = { id: caption.id, name: caption.name, content: caption.content }

          // Sync our single caption
          const syncResponse = await CaptionApi.sync({ lastSyncedAt, captions: [captionData] })
          const {
            data: { syncedAt, created, synced, updates, missing },
          } = syncResponse.data

          // IF we have local captions, update them!
          if (captions && captions.length) {
            // Create our initial set of data
            let postSyncAll = [...captions]

            // Either replace OR add an item after we sync it
            const updateItem = item => {
              const index = postSyncAll.findIndex(localItem => localItem.id === item.id)
              if (index !== -1) {
                // Update our existing local item
                postSyncAll.splice(index, 1, { ...postSyncAll[index], ...item })
              } else if (item.content) {
                // We have a new item with content which means it
                // must be an externally updated item so store it
                postSyncAll.push(item)
              }
            }

            // Update our local items
            ;[...created, ...synced, ...updates].forEach(updateItem)

            // Remove any items that were "missing" on the server
            // which means they were deleted on another device
            postSyncAll = postSyncAll.filter(item => !(missing || []).includes(item.id))

            // Store our new data
            setCaptions(postSyncAll)
            setLastSyncedAt(syncedAt)
          }

          // The UI only needs to know it worked
          resolve({ success: true })
        } catch (err) {
          reject(err)
        }
      })
    },
    [captions]
  )

  //
  // Validate our user and authenticate them if we need to
  //

  const destroy = React.useCallback(
    id => {
      return new Promise(async (resolve, reject) => {
        try {
          // If we haven't, load it from the api
          let response = await CaptionApi.delete(id)
          setCaptions(captions.filter(caption => caption.id !== id))
          resolve(response.data)
        } catch (err) {
          reject(err)
        }
      })
    },
    [captions]
  )

  //
  // Reset the local saved state
  //

  const reset = React.useCallback(() => {
    return new Promise(async (resolve, reject) => {
      try {
        setCaptions([])
        setLastSyncedAt(null)
        resolve()
      } catch (err) {
        reject(err)
      }
    })
  }, [])

  return (
    <CaptionContext.Provider value={{ captions, create, fetchAll, fetch, sync, update, destroy, reset }}>
      {children}
    </CaptionContext.Provider>
  )
}
